import { mapGetters, mapActions } from 'vuex';
import { base64UrlToJson } from '~/utils/base64-utils';
import { CHATBOT_COMMANDS } from '~/utils/chat-flags';
import ChatbotFunctions from '~/mixins/chatbot-functions';
import CheckUserElectricMeter from '~/mixins/chatbot-functions/check-user-electric-meter';
import CheckUserChangeCredential from '~/mixins/chatbot-functions/check-user-change-credential-history';
import CheckElectricityPrepaidTrxStatus from '~/mixins/chatbot-functions/check-electricity-prepaid-trx-status';
import ShowUserElectricToken from '~/mixins/chatbot-functions/show-user-electric-token';
import CheckBayarTempoState from '~/mixins/chatbot-functions/check-bayar-tempo-state';
import CheckBayarTempoReapplyDate from '~/mixins/chatbot-functions/check-bayar-tempo-reapply-date';
import ShowLinkButton from '~/mixins/chatbot-functions/show-link-button';
import CheckPhonePrepaid from '~/mixins/chatbot-functions/check-phone-prepaid';
import CheckDataPlanPrepaidTrxState from '~/mixins/chatbot-functions/check-data-plan-prepaid-trx-state';
import UnstuckDataPlanPrepaidTrx from '~/mixins/chatbot-functions/unstuck-data-plan-prepaid-trx';
import CheckWithdrawal from '~/mixins/chatbot-functions/check-withdrawal';
import CheckKirimUang from '~/mixins/chatbot-functions/check-kirim-uang';
import Pdam from '~/mixins/chatbot-functions/pdam';
import ElectricityPostpaid from '~/mixins/chatbot-functions/electricity-postpaid';
import Dana from '~/mixins/chatbot-functions/dana';
import Autorefund from '~/mixins/chatbot-functions/autorefund';

export default {
  mixins: [
    ChatbotFunctions,
    CheckUserElectricMeter,
    CheckElectricityPrepaidTrxStatus,
    ShowUserElectricToken,
    CheckUserChangeCredential,
    CheckBayarTempoState,
    CheckBayarTempoReapplyDate,
    ShowLinkButton,
    CheckPhonePrepaid,
    CheckDataPlanPrepaidTrxState,
    UnstuckDataPlanPrepaidTrx,
    CheckWithdrawal,
    CheckKirimUang,
    Pdam,
    ElectricityPostpaid,
    Dana,
    Autorefund,
  ],
  computed: {
    ...mapGetters({
      chatbotIntents: 'zopim/message/chatbotIntents',
    }),
  },
  methods: {
    ...mapActions({
      getChatbotIntent: 'zopim/message/getChatbotIntent',
      setInLiveChatSession: 'zopim/app/setInLiveChatSession',
    }),

    /**
     * Extracts chatbot command, e.g. '[apicall]', '[button]', '[resend]'
     * @param {string} message
     * @return {string} - '[apicall]', '[resend]', .etc
     */
    extractChatbotFlag(message) {
      const flag = message.substring(0, message.indexOf(']') + 1);
      return flag;
    },

    /**
     * Extracts chatbot message
     * @param {string} message - example: '[apicall] checkTransactionStatus [/apicall]'
     * @return {string} - text between the chatbot flag, example: 'checkTransactionStatus'
     */
    extractChatbotMessage(message) {
      const flag = this.extractChatbotFlag(message);
      const flagClosure = `[/${flag.substring(flag.indexOf('[') + 1, flag.indexOf(']'))}]`;
      const extractedMessage = message
        .split(flag)[1]
        .trim()
        .split(flagClosure)[0]
        .trim();

      return extractedMessage;
    },

    /**
     * Process detail object to get the right chatbot logic group
     * @param {object} detail - detail from zopim current chat logs
     */
    processDetailToChatbotLogic(detail) {
      const { msg } = detail;
      const flag = this.extractChatbotFlag(msg);

      switch (flag) {
        case '[end session]':
          this.setInLiveChatSession(false);
          this.$zopim.endChat({ clear_dept_id_on_chat_ended: true });
          break;

        // Shows
        case '[show]':
          this.processShowsCommand({ command: msg });
          break;

        // Apicall
        case '[apicall]':
          this.processApicallCommand({ command: msg });
          break;

        // Media
        case '[video]':
          this.processMediaCommand({ command: msg });
          break;
        case '[image]':
          this.processMediaCommand({ command: msg });
          break;
        default:
          break;
      }
    },

    /**
     * Run the specific shows logic based on the extracted message
     * @param {string} param.command - full msg with the format of '[show] <message> [/show]'
     */
    async processShowsCommand({ command = '' }) {
      const { shows } = CHATBOT_COMMANDS;

      switch (command) {
        case shows.showChangePhoneNumberFormLink:
          await this.showLinkButton({ linkType: 'change-phone-number' });
          break;
        default:
          break;
      }
    },

    /**
     * Run the specific apicall logic based on the extracted message
     * @param {string} param.command - full msg with the format of '[apicall] <message> [/apicall]'
     */
    async processApicallCommand({ command = '' }) {
      const { apicall } = CHATBOT_COMMANDS;

      // Get current transaction object from the 'chat' route query param
      const urlParams = new URLSearchParams(window.location.search);
      const chat = urlParams.get('chat');

      const currentTrx = chat ? base64UrlToJson(chat) : null;

      switch (command) {
        case apicall.checkAutoRefundEligibility:
          await this.checkAutoRefundEligibility(currentTrx);
          break;
        case apicall.checkKirimUangStatus:
          await this.checkKirimUangStatus(currentTrx);
          break;
        case apicall.executeRefundMitra:
          await this.executeRefundMitra(currentTrx);
          break;
        case apicall.checkKirimUangRemittedTime:
          await this.checkKirimUangRemittedTime(currentTrx);
          break;
        case apicall.checkSaldoWallet:
          await this.checkSaldoWallet();
          break;
        case apicall.checkInvoiceStatus:
          await this.checkInvoiceStatus(currentTrx);
          break;
        case apicall.checkPaymentBillings:
          await this.checkPaymentBillings(currentTrx);
          break;
        case apicall.checkUser7DaysGMV:
          await this.checkUser7DaysGMV();
          break;
        case apicall.checkUserElectricMeter:
          await this.checkUserElectricMeter(currentTrx);
          break;
        case apicall.checkElectricityPrepaidTrxStatus:
          await this.checkElectricityPrepaidTrxStatus(currentTrx);
          break;
        case apicall.showUserElectricToken:
          await this.showUserElectricToken(currentTrx);
          break;
        case apicall.checkCredentialChangeHistory:
          await this.checkCredentialChangeHistory();
          break;
        case apicall.checkBayarTempoState:
          await this.checkBayarTempoState();
          break;
        case apicall.checkBayarTempoReapplyDate:
          await this.checkBayarTempoReapplyDate();
          break;
        case apicall.checkPaidPhoneCreditDuration:
          await this.checkPaidPhoneCreditDuration(currentTrx);
          break;
        case apicall.showPhoneCreditSerialNumber:
          await this.showPhoneCreditSerialNumber(currentTrx);
          break;
        case apicall.checkDataPlanPrepaidTrxState:
          await this.checkDataPlanPrepaidTrxState(currentTrx);
          break;
        case apicall.unstuckDataPlanPrepaidTrx:
          await this.unstuckDataPlanPrepaidTrx(currentTrx);
          break;
        case apicall.checkWithdrawalStatus:
          await this.checkWithdrawalStatus(currentTrx);
          break;
        case apicall.checkWithdrawalSLA:
          await this.checkWithdrawalSLA(currentTrx);
          break;
        case apicall.checkKirimUangSLA:
          await this.checkKirimUangSLA(currentTrx);
          break;
        case apicall.checkPdamStateAndSLA:
          await this.checkPdamStateAndSLA(currentTrx);
          break;
        case apicall.checkPostpaidElectricityStateAndSLA:
          await this.checkPostpaidElectricityStateAndSLA(currentTrx);
          break;
        case apicall.checkDanaTopupStateAndSLA:
          await this.checkDanaTopupStateAndSLA(currentTrx);
          break;
        case apicall.unstuckTrxAutorefundForProcessed:
          await this.unstuckTrxAutorefundForProcessed(currentTrx);
          break;
        case apicall.checkTrxStateAutorefundForProcessed:
          await this.checkTrxStateAutorefundForProcessed(currentTrx);
          break;
        case apicall.executeAutorefundForProcessed:
          await this.executeAutorefundForProcessed(currentTrx);
          break;
        case apicall.executeAutorefundForSecondaryFailed:
          await this.executeAutorefundForSecondaryFailed(currentTrx);
          break;
        default:
          break;
      }
    },

    /**
     * Run the specific media logic
     * @param {string} param.command - full msg with the format of '[<flag>] <message> [/<flag>]'
     */
    async processMediaCommand({ command = '' }) {
      await this.showMediaFromChatbot(command);
    },

    // ----- AI Chatbot
    /**
     * Sends the chatbot intent name as a text mesage to zopim and as a tag
     * @param {object} intent
     */
    async sendAiChatbotIntent(intent = {}) {
      const intentName = intent.confidence_score < 0.8 ? 'intent_not_found' : intent.intent_name;
      const intentTag = intentName;
      const intentMsg = `[intent] ${intentName} [/intent]`;
      await this.zChatAddTags([intentTag]);
      this.$zopim.sendChatMsg(intentMsg);
    },

    /**
     * Invoke the API call to chatbot intent classifier
     * Then send it to zopim
     * @param {string} message
     */
    async handleAiChatbotIntents(message = '') {
      const payload = [{ message }];
      await this.getChatbotIntent(payload);
      const intents = [...this.chatbotIntents];
      const bestIntent = intents?.sort((a, b) => a.confidence_score - b.confidence_score).pop();
      this.sendAiChatbotIntent(bestIntent);
    },
  },
};
